import {createContext, useContext, useState} from "preact/compat";
import {useEffect} from "preact/hooks";
import {useBehavioralContextHelper} from "./behavioralContext.helper";
import {useUserContext} from "../index";

// Creates a new empty context
const BehavioralContext = createContext(null);

// TODO: most of the states in here can be refactored into reducers
//  to reduce both the code complexity and amount.
// This is the main behavioral context in which we will store the cached page data
export const BehavioralContextProvider = props => {
    // Destructured props
    const {children} = props;

    // State to control the search bar data
    const [searchBarData, setSearchBarData] = useState({
        isOpen:              false,
        query:               "",
        data:                [],
        recommendedProducts: null   // Legacy property
    });
    // State to control the sidebar cart data state
    const [sidebarCartData, setSidebarCartData] = useState({
        isOpen:                   false,
        // Object in which a product's data such as price and id
        // will be stored in before actually being added to cart.
        newProductData:           {},
        // This is a property that will only be used to add items to the cart
        // outside the product page.
        url:                      "",
        // This is a property to store the last warehouse that has been added to the cart
        // to automatically add new products from the same warehouse into the cart.
        lastWarehouseAdded:       {},
        // Controls the same warehouse feature modal display state
        isSameWarehouseModalOpen: false,
        // State to flag whenever we should bypass the same warehouse modal activation
        modalBypass:              false
    });
    // State to control the sidebar menu display state
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // State to control the new item alert status
    const [newItemAlertStatus, setNewItemAlertStatus] = useState({
        isOpen:      false,
        productName: ""
    });
    // Perhaps this is used to display the success message at the top of the page
    // when a user subscribes to the newsletter.
    const [newsLetterSubmitted, setNewsLetterSubmitted] = useState(false);

    // Destructured cart data from the user context
    const [{}, {}, {cartData, acceptedTermUse}] = useUserContext();

    // Destructured cart data
    // eslint-disable-next-line no-unused-vars
    const {cartId, qtyItems} = cartData;

    // Procedure to handle the isSearchOpen state
    const handleSearchBar = (
        target = undefined,
        state =  undefined
    ) => {
        // Prevents any unwanted errors
        if (!target) {
            return;
        }

        // Prevents any unwanted assignment errors
        if (!Object.keys(searchBarData).includes(target)) {
            return;
        }

        // Updates the target property according to the given target as the procedure prop
        setSearchBarData(prevState => {
            return {
                ...prevState,
                [target]: state
            }
        })
    }

    // Procedure to handle the sidebarCartData state
    const handleSidebarCartData = (
        target =  undefined,
        state =   undefined,
        // This is a special prop only to be used
        // when needed to assign multiple data at the same time.
        dataSet = undefined
    ) => {
        // Prevents unwanted errors
        if (!target) {
            return;
        }

        // Checks whether it should fetch for the product data
        // or display the same warehouse modal
        if (
            !dataSet?.modalBypass &&
            Object.keys(sidebarCartData.lastWarehouseAdded).length > 0 &&
            qtyItems > 0 &&
            (
                dataSet?.isOpen ||
                dataSet?.url
            )
        ) {
            // Special validation for when the user is at the product page.
            // This isn't the best of the practices, but at least it works for now.
            if (dataSet?.source === "productPage") {
                // If the new product has the same warehouse as the last one added,
                // then it opens the same warehouse modal component.
                // Otherwise, it just follows as normal.
                if (dataSet.newProductData.productData.variants.find(item => {
                    // return item.id === sidebarCartData.lastWarehouseAdded.data.id;
                    return item.name.toLowerCase().includes(
                        sidebarCartData.lastWarehouseAdded.data.label.toLowerCase()
                    );
                }) && acceptedTermUse) {
                    dataSet.isSameWarehouseModalOpen = true;
                    dataSet.isOpen =                   false;
                } else {
                    dataSet.isSameWarehouseModalOpen = false;
                    dataSet.isOpen =                   true;
                }
            } else {
                productDataFetcher(dataSet).then(res => {
                    if (!res) {
                        return;
                    }

                    setSidebarCartData(prevState => {
                        return {
                            ...prevState,
                            ...res
                        }
                    });
                });

                return;
            }
        }

        // Updates the "sidebarCartData" state with the new data
        setSidebarCartData(prevState => {
            return !dataSet ? {
                ...prevState,
                [target]: typeof state !== "undefined" ? state : prevState["target"],
                // Resets the newProductData property when the cart is closed
                ...((target === "isOpen" && !state) && {
                    newProductData: {}
                })
            } : {
                ...prevState,
                ...dataSet
            }
        });
    }

    // Helper data for the current context
    const contextHelper = useBehavioralContextHelper({
        sidebarCartData,
        handleSidebarCartData
    });

    // Retrieves the product fetcher method from the context helper
    const {productDataFetcher} = contextHelper;

    // Procedure to handle the isMenuOpen state
    const handleMenu = (
        state = undefined
    ) => {
        setIsMenuOpen(prevState => {
            return state || !prevState;
        });
    }

    // Procedure to handle the newItemAlertStatus state
    const handleNewItemAlertStatus = (
        data = undefined,
        action = "default"
    ) => {
        // Special validation to clear the state
        if (action === "clear") {
            // Clears all data after four seconds
            const timer = setTimeout(() => {
                setNewItemAlertStatus({
                    isOpen:      false,
                    productName: ""
                })

                clearTimeout(timer);
            }, 4000);

            return;
        }

        if (!data) {
            return;
        }

        setNewItemAlertStatus({
            ...data
        });
    }



    // Data variable that will be soon returned by the context
    const data = {
        searchBarData,
        sidebarCartData,
        isMenuOpen,
        newItemAlertStatus,
        newsLetterSubmitted,
        handleSearchBar,
        // TODO: rename this property name throughout the whole application
        handleCart: handleSidebarCartData,
        handleMenu,
        handleNewItemAlertStatus,
        setNewsLetterSubmitted
    };

    // Effect to be triggered when the new item alert is open to clear its data
    useEffect(() => {
        if (!newItemAlertStatus["isOpen"]) {
            return;
        }

        // Calls the procedure in "clear" mode
        handleNewItemAlertStatus(
            null,
            "clear"
        );
    }, [newItemAlertStatus]);

    // --- FOR DEBUGGING PURPOSES ONLY ---
    // useEffect(() => {
    //     console.log("DEBUGGER", sidebarCartData);
    // }, [sidebarCartData]);

    return (
        <BehavioralContext.Provider value={data}>
            {children}
        </BehavioralContext.Provider>
    )
}

// Hook to provide a simple context accessibility
const useBehavioralContext = () => useContext(BehavioralContext);
export default useBehavioralContext;
